import React from 'react'
import { useChatStore } from '../lib/store'
import { MCPServerStatus } from '../types/mcp'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  onConfigOpen: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, onConfigOpen }) => {
  const { 
    sessions, 
    currentSessionId, 
    selectSession, 
    createSession, 
    deleteSession,
    selectedProvider,
    selectedModel,
    mcpServers,
    mcpConnections,
    connectMCPServer,
    disconnectMCPServer
  } = useChatStore()

  const handleNewChat = () => {
    if (selectedProvider && selectedModel) {
      createSession(selectedProvider, selectedModel)
    } else {
      onConfigOpen()
    }
  }

  const handleMCPToggle = async (serverId: string) => {
    const connection = mcpConnections[serverId]
    if (connection?.status === MCPServerStatus.Connected) {
      disconnectMCPServer(serverId)
    } else {
      await connectMCPServer(serverId)
    }
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:relative inset-y-0 left-0 z-50 w-72 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Chat Sessions
              </h2>
              <button
                onClick={handleNewChat}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                title="New Chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {sessions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                No chat sessions yet
              </p>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  className={`group relative p-3 rounded-lg cursor-pointer transition-colors ${
                    session.id === currentSessionId
                      ? 'bg-primary-100 dark:bg-primary-900/20 border border-primary-300 dark:border-primary-700'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => selectSession(session.id)}
                >
                  <div className="pr-8">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {session.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {session.provider} • {session.model}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {session.updatedAt.toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteSession(session.id)
                    }}
                    className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity"
                    title="Delete session"
                  >
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>

          {/* MCP Servers Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              MCP Servers
            </h3>
            <div className="space-y-2">
              {mcpServers.map((server) => {
                const connection = mcpConnections[server.id]
                const status = connection?.status || MCPServerStatus.Disconnected
                
                return (
                  <div
                    key={server.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {server.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {status}
                      </p>
                    </div>
                    <button
                      onClick={() => handleMCPToggle(server.id)}
                      className={`ml-2 px-3 py-1 text-xs rounded-full transition-colors ${
                        status === MCPServerStatus.Connected
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : status === MCPServerStatus.Connecting
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                      disabled={status === MCPServerStatus.Connecting}
                    >
                      {status === MCPServerStatus.Connected
                        ? 'Disconnect'
                        : status === MCPServerStatus.Connecting
                        ? 'Connecting...'
                        : 'Connect'}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <button
              onClick={onConfigOpen}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
